import * as Sentry from '@sentry/vue';
import { safeStringify } from '@/utils/jsonHelpers';

type Attributes = Record<string, string | number | boolean>;

function toAttributes(extra: unknown[]): Attributes | undefined {
  if (extra.length === 0) return undefined;
  const attributes: Attributes = {};
  for (const [index, value] of extra.entries()) {
    const key = `arg${index}`;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      attributes[key] = value;
    } else {
      attributes[key] = safeStringify(value);
    }
  }
  return attributes;
}

export const logger = {
  debug(message: string, ...extra: unknown[]): void {
    if (!Sentry.isEnabled()) {
      // eslint-disable-next-line no-console -- sanctioned console sink whenever Sentry is off (local dev)
      console.debug(message, ...extra);
      return;
    }
    Sentry.logger.debug(message, toAttributes(extra));
  },

  info(message: string, ...extra: unknown[]): void {
    if (!Sentry.isEnabled()) {
      // eslint-disable-next-line no-console -- sanctioned console sink whenever Sentry is off (local dev)
      console.info(message, ...extra);
      return;
    }
    Sentry.logger.info(message, toAttributes(extra));
  },

  warn(message: string, ...extra: unknown[]): void {
    if (!Sentry.isEnabled()) {
      // eslint-disable-next-line no-console -- sanctioned console sink whenever Sentry is off (local dev)
      console.warn(message, ...extra);
      return;
    }
    Sentry.logger.warn(message, toAttributes(extra));
  },

  error(message: string, error?: unknown, ...extra: unknown[]): void {
    if (!Sentry.isEnabled()) {
      // eslint-disable-next-line no-console -- sanctioned console sink whenever Sentry is off (local dev)
      console.error(message, error, ...extra);
      return;
    }
    Sentry.logger.error(message, {
      ...toAttributes(extra),
      ...(error === undefined
        ? {}
        : { error: error instanceof Error ? error.message : safeStringify(error) }),
    });
    if (error !== undefined) {
      Sentry.captureException(error, { extra: { message, ...toAttributes(extra) } });
    }
  },
};
